import { useLazyQuery, useMutation } from '@apollo/client';
import { accounts, CredentialResponse } from 'google-one-tap';
import { User } from 'graphql.generated';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { USER_QUERY } from 'queries/user/user.query';
import { useState } from 'react';
import {
    trackGoogleOneTapPromptError,
    trackGoogleOneTapSuccess
} from 'shared/components/AuthModal/section/GoogleOneTap/index.tracking';
import { triggerSuccessfulAuthentication } from 'shared/components/AuthModal/utils/triggerSuccessfulAuthentication';
import { CONSENT_COOKIE } from 'shared/components/AuthPrompts/AuthPrompts.constants';
import { consentNotShownInLastTenMins } from 'shared/components/AuthPrompts/utils/consentNotShownInLastTenMins';
import { GOOGLE_ONE_TAP_SIGN_IN_MUTATION } from '../../../queries/googleOneTap';
import { SignInAction } from '../useSocialSignIn.types';
import { GoogleOneTapMutation } from './useGoogleOneTap.types';
import { isNewUser } from 'shared/components/AuthModal/utils';
import { trackAuthSegmentEvent } from 'shared/components/AuthModal/utils/segmentTrack';
import { getSegmentData } from 'shared/components/AuthModal/utils/getSegmentData';

export const useGoogleOneTap = (
    { onSuccess }: SignInAction,
    currentUser: User = {}
) => {
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const router = useRouter();

    const init = () => {
        if (!window.google) {
            return;
        }
    };

    const [triggerSocialUserTracking] = useLazyQuery<{ user: User }>(
        USER_QUERY,
        {
            onCompleted(data) {
                if (isNewUser(currentUser, data?.user)) {
                    trackGoogleOneTapSuccess('registration');
                    trackAuthSegmentEvent(
                        'reg',
                        getSegmentData(router.pathname)
                    );
                } else {
                    trackGoogleOneTapSuccess('signin');
                    trackAuthSegmentEvent(
                        'signin',
                        getSegmentData(router.pathname)
                    );
                }

                onSuccess(data?.user);
            },
            onError() {
                onSuccess();
            },
            // force the request to be fulfilled by the server, as it already ran once before
            fetchPolicy: 'network-only'
        }
    );

    const [signInMutation] = useMutation<GoogleOneTapMutation>(
        GOOGLE_ONE_TAP_SIGN_IN_MUTATION,
        {
            onCompleted({ socialSignInGoogleOneTap: { success } }) {
                if (success === 0) {
                    setIsAuthenticating(false);
                    trackGoogleOneTapPromptError(router.asPath);
                    return;
                }
                triggerSuccessfulAuthentication();
                triggerSocialUserTracking();
            },
            onError() {
                setIsAuthenticating(false);
                trackGoogleOneTapPromptError(router.asPath);
            }
        }
    );

    const signInToZooplaWithGoogle = (
        credentialResponse: CredentialResponse
    ): void => {
        if (credentialResponse) {
            signInMutation({
                variables: {
                    accessToken: credentialResponse.credential
                }
            });
        }
    };

    const onLogin = () => {
        // using sessionStorage here to get login status from universal header
        // default user value in app-react is logged out, i.e. 100% triggers the Google One Tap prompt
        const isGuestUser =
            sessionStorage?.getItem('z-bui-component-header-is-logged-in') ===
            'false';
        const consentCookie = Cookies.get(CONSENT_COOKIE);

        if (!window.google) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const googleAccounts: accounts = ((google as unknown) as any)?.accounts;

        if (consentNotShownInLastTenMins(consentCookie) && isGuestUser) {
            // Note: TS get namespace `google` from the Google Maps package caused type error
            googleAccounts?.id.initialize({
                client_id:
                    '696203622959-7kq1qaikjuk02838oc5ckv0t3a4329tq.apps.googleusercontent.com',
                context: 'signin',
                callback: signInToZooplaWithGoogle
            });

            googleAccounts?.id.prompt((notification) => {
                if (
                    notification.isNotDisplayed() ||
                    notification.isSkippedMoment()
                ) {
                    console.log(notification.getNotDisplayedReason());
                }
            });
        }
    };

    return {
        init,
        onLogin,
        isAuthenticating
    };
};
